import { useEffect, useMemo, useState } from "react";
import { Show, useAuth } from "@clerk/react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Trash2, Crown } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { makeApi, publicApi } from "@/lib/api";
import { isAndroidApp } from "@/lib/platform";

const BLANK_MCQ = () => ({ _key: crypto.randomUUID(), type: "mcq", text: "", options: ["", "", "", ""], correct_index: 0 });
const BLANK_TF = () => ({ _key: crypto.randomUUID(), type: "tf", text: "", options: ["True", "False"], correct_index: 0 });

export default function Submit() {
  const { getToken, isSignedIn } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [cats, setCats] = useState([]);
  const [title, setTitle] = useState("");
  const [proposedCategory, setProposedCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");
  const [qs, setQs] = useState([BLANK_MCQ()]);
  const [mine, setMine] = useState([]);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => { publicApi.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { if (isSignedIn) api.get("/me").then((r) => setMe(r.data)).catch(() => {}); }, [isSignedIn, api]);
  const locked = me && !me.subscribed && me.role !== "admin";
  useEffect(() => { if (isSignedIn) api.get("/submissions/mine").then((r) => setMine(r.data)); }, [isSignedIn, api]);

  const updateQ = (i, patch) => setQs((prev) => prev.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updateOpt = (i, oi, val) => setQs((prev) => prev.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, j) => (j === oi ? val : o)) } : q));
  const addMcq = () => setQs((p) => [...p, BLANK_MCQ()]);
  const addTf = () => setQs((p) => [...p, BLANK_TF()]);
  const removeQ = (i) => setQs((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!title.trim()) return toast.error("Add a quiz title");
    if (qs.length === 0) return toast.error("Add at least 1 question");
    for (const q of qs) {
      if (!q.text.trim()) return toast.error("Fill in every question");
      if (q.type === "mcq" && q.options.some((o) => !o.trim())) return toast.error("Fill in all 4 options for MCQ");
    }
    setSaving(true);
    try {
      await api.post("/submissions", { quiz_title: title, category_id: categoryId || null, proposed_category: proposedCategory || null, notes, questions: qs.map(({ _key, ...q }) => q) });
      toast.success("Submission sent! An admin will review it.");
      setTitle(""); setNotes(""); setProposedCategory(""); setCategoryId(""); setQs([BLANK_MCQ()]);
      const r = await api.get("/submissions/mine"); setMine(r.data);
    } catch (e) {
      toast.error("Could not submit. Try again.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      <Show when="signed-in">
        <Nav />
        {locked ? (
          <main className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="submit-paywall">
            <Crown size={48} strokeWidth={2.5} className="mx-auto text-[var(--tp-success)]" />
            <h1 className="mt-4 text-3xl font-black">Submitting quizzes is a Supporter perk</h1>
            {isAndroidApp() ? (
              <p className="mt-2 text-slate-700" data-testid="paywall-android-note">Supporter plans can't be bought in the Android app. If you're already a Supporter, your perks work here automatically.</p>
            ) : (<>
            <p className="mt-2 text-slate-700">For $2.99/month you can share your study materials, play ad-free with unlimited lives, and help keep TP running.</p>
            <Link to="/pricing" data-testid="paywall-go-pro"
              className="mt-8 inline-block px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151]">
              Become a Supporter
            </Link>
            </>)}
          </main>
        ) : (
        <main className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-4xl font-black tracking-tight">Submit a quiz</h1>
          <p className="text-slate-600 mt-1">Teachers & students — share your study materials with the TP community.</p>

          <div className="sticker-card p-6 mt-8 space-y-5">
            <div>
              <label className="font-black text-sm">Quiz title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="submit-title"
                className="mt-1 w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" placeholder="e.g. World War II basics" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-black text-sm">Existing category (optional)</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="submit-category"
                  className="mt-1 w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold bg-white">
                  <option value="">— pick one —</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="font-black text-sm">Or propose a new category</label>
                <input value={proposedCategory} onChange={(e) => setProposedCategory(e.target.value)}
                  className="mt-1 w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" placeholder="e.g. Spanish verbs" />
              </div>
            </div>

            <div>
              <label className="font-black text-sm">Notes to admin (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" rows={2} />
            </div>
          </div>

          <h2 className="text-2xl font-black tracking-tight mt-10 mb-3">Questions</h2>

          <div className="space-y-5">
            {qs.map((q, i) => (
              <div key={q._key} className="sticker-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-block px-3 py-1 rounded-full border-[2px] border-[#374151] bg-[#FFF4C8] font-black text-xs uppercase">
                    #{i + 1} • {q.type === "mcq" ? "Multiple choice" : "True / False"}
                  </span>
                  {qs.length > 1 && (
                    <button onClick={() => removeQ(i)} className="text-[#FF4B4B]" data-testid={`remove-q-${i}`}>
                      <Trash2 size={18} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
                <input value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} data-testid={`q-text-${i}`}
                  placeholder="Question text" className="w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" />
                <div className="mt-3 space-y-2">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi}
                        onChange={() => updateQ(i, { correct_index: oi })} data-testid={`q-${i}-correct-${oi}`}
                        className="w-5 h-5" />
                      {q.type === "mcq" ? (
                        <input value={o} onChange={(e) => updateOpt(i, oi, e.target.value)}
                          placeholder={`Option ${oi + 1}`}
                          className="flex-1 px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold" />
                      ) : (
                        <span className="font-black">{o}</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">Tap the radio button next to the correct answer.</p>
              </div>
            ))}
          </div>

          <div className="mt-5 flex gap-2 flex-wrap">
            <button onClick={addMcq} data-testid="add-mcq"
              className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none inline-flex items-center gap-1">
              <Plus size={16} strokeWidth={2.5} /> Add MCQ
            </button>
            <button onClick={addTf} data-testid="add-tf"
              className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none inline-flex items-center gap-1">
              <Plus size={16} strokeWidth={2.5} /> Add True/False
            </button>
          </div>

          <div className="mt-8">
            <button onClick={submit} disabled={saving} data-testid="submit-quiz"
              className="px-8 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none disabled:opacity-60">
              {saving ? "Submitting…" : "Submit for review"}
            </button>
          </div>

          <h2 className="text-2xl font-black tracking-tight mt-14 mb-3">My submissions</h2>
          <div className="sticker" data-testid="my-submissions">
            {mine.length === 0 && <p className="p-5 text-slate-500">No submissions yet.</p>}
            {mine.map((s, i) => (
              <div key={s.id} className={`p-4 ${i !== mine.length - 1 ? "border-b-[2px] border-slate-200" : ""} flex items-center justify-between`}>
                <div>
                  <p className="font-black">{s.quiz_title}</p>
                  <p className="text-sm text-slate-500">{s.questions.length} question(s)</p>
                </div>
                <span className={`px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${
                  s.status === "approved" ? "bg-[var(--tp-success)] text-white" :
                  s.status === "rejected" ? "bg-[#FF4B4B] text-white" : "bg-[var(--tp-accent)]"
                }`}>{s.status}</span>
              </div>
            ))}
          </div>
        </main>
        )}
      </Show>
    </>
  );
}
